import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AudioPlayer from './AudioPlayer';
import Afiliados from './Afiliados';
import AdUnit from './AdUnit';
import PayPalButton from './PayPalButton';
import MusicaAmbiental from './MusicaAmbiental';
import { useLectura } from '../context/LecturaContext';
import { validarCodigoPromo } from '../utils/api';

function Resultado() {
  const navigate = useNavigate();
  const { resultado, limpiarResultado, desbloquear } = useLectura();
  const [textoVisible, setTextoVisible]   = useState(0);
  const [codigoInput, setCodigoInput]     = useState('');
  const [validando, setValidando]         = useState(false);
  const [errorCodigo, setErrorCodigo]     = useState('');
  const [mostrarCodigo, setMostrarCodigo] = useState(false);
  const [errorPago, setErrorPago]         = useState('');
  const [revelando, setRevelando]         = useState(false);

  useEffect(() => {
    if (!resultado) { navigate('/'); return; }
    let t = 0;
    const avanzar = () => {
      t++;
      setTextoVisible(t);
      if (t < 3) setTimeout(avanzar, 1600);
    };
    const inicio = setTimeout(avanzar, 800);
    return () => clearTimeout(inicio);
  }, [resultado, navigate]);

  useEffect(() => {
    if (!revelando) return;
    const t = setTimeout(() => {
      setRevelando(false);
      requestAnimationFrame(() => {
        setTimeout(() => desbloquear(), 50);
      });
    }, 1800);
    return () => clearTimeout(t);
  }, [revelando, desbloquear]);

  if (!resultado) return null;

  const { nombre, tipoMano, intencion, mano, ladoMano, esPremium, lectura, audios } = resultado;

  const iconosIntencion  = { amor: '\u2764\uFE0F', salud: '\uD83D\uDC9A', trabajo: '\uD83D\uDCBC' };
  const nombresIntencion = { amor: 'Amor y relaciones', salud: 'Salud y bienestar', trabajo: 'Trabajo y \u00E9xito' };
  const nombresMano      = { tierra: '\uD83C\uDF0D Mano de Tierra', agua: '\uD83D\uDCA7 Mano de Agua', aire: '\uD83D\uDCA8 Mano de Aire', fuego: '\uD83D\uDD25 Mano de Fuego' };

  const handleDesbloquear = async (e) => {
    e.preventDefault();
    if (!codigoInput.trim()) return;
    setValidando(true);
    setErrorCodigo('');
    try {
      await validarCodigoPromo(codigoInput.trim().toUpperCase());
      setRevelando(true);
    } catch (err) {
      setErrorCodigo(err.response?.data?.error || 'C\u00F3digo no v\u00E1lido. Int\u00E9ntalo de nuevo.');
    } finally {
      setValidando(false);
    }
  };

  return (
    <div className="contenedor" style={{ paddingBottom: '2rem' }}>
      <MusicaAmbiental />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>

        {/* Audio Intro */}
        {audios?.intro && <AudioPlayer src={audios.intro} autoPlay={true} />}

        {/* Cabecera */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring' }}
            style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            {iconosIntencion[intencion]}
          </motion.div>
          <h1 className="titulo" style={{ fontSize: '2rem' }}>Tu lectura, {nombre}</h1>
          <p style={{ color: 'var(--color-texto-suave)' }}>
            {nombresIntencion[intencion]} &bull; {nombresMano[tipoMano]}
            {ladoMano && <span style={{ marginLeft: '0.5rem' }}>&bull; {ladoMano === 'derecha' ? '\u270D\uFE0F derecha' : '\uD83E\uDD1A izquierda'}</span>}
            {mano && <span style={{ marginLeft: '0.5rem' }}>&bull; {mano === 'diestra' ? 'activa' : 'del alma'}</span>}
          </p>
          {esPremium && (
            <span style={{ background: 'var(--color-oro)', color: 'var(--color-fondo)', padding: '0.3rem 1rem',
              borderRadius: '20px', fontSize: '0.8rem', fontWeight: 'bold', marginTop: '0.5rem', display: 'inline-block' }}>
              ✨ REVELACIÓN COMPLETA
            </span>
          )}
        </div>

        <div style={{ maxWidth: '600px', margin: '0 auto' }}>

          {/* SECCI\u00D3N 1 \u2014 Intro */}
          {textoVisible >= 1 && (
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
              style={{ background: 'var(--color-secundario)', padding: '1.5rem', borderRadius: '12px',
                marginBottom: '1.5rem', borderLeft: '4px solid var(--color-oro)' }}>
              <p style={{ fontSize: '1.1rem', fontStyle: 'italic', color: 'var(--color-oro)' }}>
                "{lectura.intro}"
              </p>
            </motion.div>
          )}

          {/* SECCI\u00D3N 2 \u2014 Mano */}
          {textoVisible >= 2 && lectura.manoTexto && (
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
              style={{ background: 'rgba(255,215,0,0.06)', padding: '1.5rem', borderRadius: '12px',
                marginBottom: '1.5rem', border: '1px solid rgba(255,215,0,0.25)' }}>
              <h3 style={{ color: 'var(--color-oro)', marginBottom: '0.5rem' }}>
                {mano === 'diestra' ? '\u270D\uFE0F Tu mano activa habla\u2026' : '\uD83E\uDD1A Tu mano del alma revela\u2026'}
              </h3>
              <p style={{ fontStyle: 'italic' }}>{lectura.manoTexto}</p>
            </motion.div>
          )}

          {/* SECCI\u00D3N 3 \u2014 General con acantilado emocional */}
          {textoVisible >= 3 && (
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
              style={{ marginBottom: 0, position: 'relative',
                ...(esPremium ? {} : { maxHeight: '4.2rem', overflow: 'hidden' }) }}>
              <p style={{ lineHeight: 1.75, margin: 0 }}>{lectura.general}</p>
              {/* Sombra: arranca desde el 30% — corta en la 2ª línea */}
              {!esPremium && (
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '80%',
                  background: 'linear-gradient(to bottom, transparent 0%, var(--color-fondo) 75%)',
                  pointerEvents: 'none' }} />
              )}
            </motion.div>
          )}

          <AnimatePresence mode="wait">
          {textoVisible >= 3 && !esPremium && !revelando && (
            <motion.div key="bloqueado" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.5, delay: 0.2 }}>

              {/* Anzuelo — frase personalizada generada por la IA */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
                style={{ textAlign: 'center', padding: '1.8rem 0.5rem 1.4rem' }}>
                <p style={{ color: 'var(--color-oro)', fontStyle: 'italic', fontSize: '1.1rem', lineHeight: 1.8, margin: 0 }}>
                  {lectura.teaser
                    ? <>{lectura.teaser}<span style={{ opacity: 0.6 }}>...</span></>
                    : <>"Hay algo que tus manos llevan tiempo<br/>
                        intentando decirte, <strong>{nombre}</strong>.<br/>
                        <span style={{ fontSize: '0.95rem', opacity: 0.85 }}>Algo que nadie más se atrevió a nombrar."</span>
                      </>
                  }
                </p>
              </motion.div>

              {/* CTA \u2014 limpio, directo, sin ruido */}
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
                style={{
                  padding: '2rem 1.5rem',
                  background: 'linear-gradient(160deg, rgba(255,215,0,0.07) 0%, rgba(8,8,18,0.98) 100%)',
                  borderRadius: '18px',
                  border: '1px solid rgba(255,215,0,0.28)',
                  textAlign: 'center',
                  boxShadow: '0 8px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,215,0,0.05)'
                }}>

                <div style={{ fontSize: '2.2rem', marginBottom: '0.6rem', filter: 'drop-shadow(0 0 12px rgba(255,215,0,0.4))' }}>🔮</div>
                <h3 style={{ color: 'var(--color-oro)', fontSize: '1.25rem', marginBottom: '0.35rem',
                  fontFamily: 'var(--fuente-titulo)', letterSpacing: '0.03em' }}>
                  Tu Revelación Completa
                </h3>
                <p style={{ fontSize: '0.83rem', color: 'var(--color-texto-suave)', lineHeight: 1.65,
                  maxWidth: '260px', margin: '0 auto 1.4rem' }}>
                  Lo que Selene ve y no puede callar.<br/>
                  El consejo exacto para tu momento.<br/>
                  <span style={{ opacity: 0.7 }}>Con audio en su voz.</span>
                </p>

                <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: '0.4rem',
                  background: 'rgba(255,215,0,0.1)', border: '1px solid rgba(255,215,0,0.3)',
                  borderRadius: '50px', padding: '0.5rem 1.6rem', marginBottom: '1.6rem' }}>
                  <span style={{ fontFamily: 'var(--fuente-titulo)', fontSize: '2rem', fontWeight: '700',
                    color: 'var(--color-oro)', lineHeight: 1 }}>$1.99</span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--color-texto-suave)' }}>USD · pago único</span>
                </div>

                <PayPalButton
                  onSuccess={(detalles) => {
                    console.log('Pago exitoso:', detalles);
                    setRevelando(true);
                  }}
                  onError={(msg) => setErrorPago(msg)}
                />

                {errorPago && (
                  <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    style={{ color: 'var(--color-acento)', marginTop: '0.7rem', fontSize: '0.82rem' }}>
                    {errorPago}
                  </motion.p>
                )}

                {/* C\u00F3digo promo \u2014 discreto, no compite con el pago */}
                <div style={{ marginTop: '1.4rem' }}>
                  {!mostrarCodigo ? (
                    <button onClick={() => setMostrarCodigo(true)}
                      style={{ background: 'none', border: 'none', color: 'rgba(255,215,0,0.35)',
                        fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline', padding: '0.2rem' }}>
                      Tengo un código de acceso
                    </button>
                  ) : (
                    <>
                      <form onSubmit={handleDesbloquear}
                        style={{ display: 'flex', gap: '0.6rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <input
                          type="text"
                          value={codigoInput}
                          onChange={e => { setCodigoInput(e.target.value.toUpperCase()); setErrorCodigo(''); }}
                          placeholder="CÓDIGO"
                          maxLength={20}
                          autoFocus
                          style={{ flex: 1, minWidth: '140px', maxWidth: '200px', padding: '0.65rem 1rem',
                            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,215,0,0.35)',
                            borderRadius: '8px', color: 'var(--color-texto)', fontSize: '0.9rem',
                            textAlign: 'center', letterSpacing: '2px', outline: 'none' }}
                        />
                        <button type="submit" className="boton" disabled={validando}
                          style={{ padding: '0.65rem 1.2rem', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                          {validando ? '\uD83D\uDD2E...' : 'Usar'}
                        </button>
                      </form>
                      {errorCodigo && (
                        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                          style={{ color: 'var(--color-acento)', marginTop: '0.5rem', fontSize: '0.8rem' }}>
                          {errorCodigo}
                        </motion.p>
                      )}
                    </>
                  )}
                </div>
              </motion.div>

            </motion.div>
          )}

          {/* Animaci\u00F3n de revelaci\u00F3n */}
          {revelando && (
            <motion.div key="revelando" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ textAlign: 'center', padding: '3rem 1rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔮</div>
              <p style={{ color: 'var(--color-oro)', fontStyle: 'italic', fontSize: '1.1rem' }}>
                Selene levanta el velo…
              </p>
            </motion.div>
          )}

          {/* Secciones premium visibles al desbloquear */}
          {esPremium && (
            <motion.div key="desbloqueado" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
                style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ color: 'var(--color-acento)', marginBottom: '0.5rem' }}>
                  En tu {nombresIntencion[intencion].toLowerCase()}
                </h3>
                <p>{lectura.especifica}</p>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
                style={{ background: 'rgba(255,215,0,0.06)', padding: '1.5rem', borderRadius: '12px',
                  marginBottom: '2rem', border: '1px solid rgba(255,215,0,0.25)' }}>
                <h3 style={{ color: 'var(--color-oro)', marginBottom: '0.5rem' }}>🌙 El consejo de Selene</h3>
                <p style={{ fontWeight: '500' }}>{lectura.consejo}</p>
              </motion.div>

              {/* Outro */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.6 }}
                style={{ textAlign: 'center', marginTop: '2rem' }}>
                <div style={{ fontSize: '1.1rem', color: 'var(--color-oro)', fontStyle: 'italic',
                  padding: '2rem', borderTop: '1px solid rgba(255,215,0,0.25)', borderBottom: '1px solid rgba(255,215,0,0.25)',
                  lineHeight: 1.8 }}>
                  "{lectura.outro}"
                </div>
                {audios?.outro && <div style={{ marginTop: '1.5rem' }}><AudioPlayer src={audios.outro} autoPlay={false} /></div>}
              </motion.div>
            </motion.div>
          )}
          </AnimatePresence>

          {/* Botones finales */}
          {textoVisible >= 3 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
              style={{ marginTop: '0.5rem', display: 'flex', gap: '0.8rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="boton"
                onClick={() => {
                  const texto = `${lectura.textoCompleto}\n\n\u2014 Selene, Tu Mano Dice`;
                  if (navigator.share) { navigator.share({ title: 'Mi lectura de mano', text: texto }); }
                  else { navigator.clipboard.writeText(texto); alert('\u00A1Copiado! Comparte tu lectura.'); }
                }}>🔗 Compartir lectura</button>
              <button className="boton"
                style={{ background: 'rgba(255,215,0,0.06)', border: '1px solid rgba(255,215,0,0.2)' }}
                onClick={() => navigate('/')}>
                🌙 Nueva lectura
              </button>
            </motion.div>
          )}

          {/* Afiliados Amazon */}
          {textoVisible >= 3 && <Afiliados />}

          {/* Publicidad al fondo */}
          {textoVisible >= 3 && (
            <div style={{ marginTop: '1rem' }}>
              <AdUnit />
            </div>
          )}

        </div>
      </motion.div>
    </div>
  );
}

export default Resultado;